import type { SettingsState, SettingsProviderProps } from '../types';

import { isEqual } from 'es-toolkit';
import { useMemo, useState, useCallback } from 'react';

import { SettingsContext } from './settings-context';

// ----------------------------------------------------------------------

export function SettingsProvider({
  children,
  defaultSettings: defaultSettingsProp,
}: SettingsProviderProps) {
  const [state, setState] = useState<SettingsState>(defaultSettingsProp);

  const resetState = useCallback(() => {
    setState(defaultSettingsProp);
  }, [defaultSettingsProp]);

  const setField = useCallback(
    (name: keyof SettingsState, value: SettingsState[keyof SettingsState]) => {
      setState((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSetState = useCallback((updateValue: Partial<SettingsState>) => {
    setState((prev) => ({ ...prev, ...updateValue }));
  }, []);

  const [openDrawer, setOpenDrawer] = useState(false);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(state, defaultSettingsProp);

  const onReset = useCallback(() => {
    resetState();
  }, [resetState]);

  const memoizedValue = useMemo(
    () => ({
      canReset,
      onReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      state,
      setState: handleSetState,
      setField,
    }),
    [canReset, onReset, openDrawer, onCloseDrawer, onToggleDrawer, state, handleSetState, setField]
  );

  return <SettingsContext value={memoizedValue}>{children}</SettingsContext>;
}
